import React, { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Spinner } from '@billda/design-system';
import { useInvitationFlowStore } from '../stores/invitationFlowStore';
import ProfileCaptureForm from '../components/ProfileCaptureForm';
import { AuthLayout } from '../../../shared/components/auth';

/**
 * Page for capturing user profile information during invitation flow
 */
const ProfileCapturePage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  // Get invitation data and actions from store
  const invitationInfo = useInvitationFlowStore(state => state.invitationInfo);
  const markProfileCaptured = useInvitationFlowStore(
    state => state.markProfileCaptured
  );

  // Check prerequisites and redirect if needed
  useEffect(() => {
    if (!id) {
      navigate('/invitation/error', { replace: true });
      return;
    }

    if (!invitationInfo) {
      // No invitation info, redirect to loading page
      navigate(`/invitation/${id}`, { replace: true });
      return;
    }

    if (!invitationInfo.requiresProfileCapture) {
      // Profile capture not required, skip to next step
      if (invitationInfo.requiresDataSharingConsent) {
        navigate(`/invitation/${id}/consent`, { replace: true });
      } else {
        navigate(`/invitation/${id}/processing`, { replace: true });
      }
    }
  }, [id, invitationInfo, navigate]);

  // Handle successful profile capture
  const handleProfileSuccess = () => {
    // Mark profile as captured in store
    markProfileCaptured();

    // Navigate to next step
    if (invitationInfo?.requiresDataSharingConsent) {
      navigate(`/invitation/${id}/consent`);
    } else {
      navigate(`/invitation/${id}/processing`);
    }
  };

  // Show loading if prerequisites not met
  if (!invitationInfo || !id) {
    return (
      <AuthLayout variant="invite">
        <div className="flex min-h-[200px] items-center justify-center">
          <Spinner />
        </div>
      </AuthLayout>
    );
  }

  // Render the profile capture form
  return (
    <AuthLayout
      variant="invite"
      organizationName={invitationInfo.organisationName}
    >
      <ProfileCaptureForm
        invitationId={id}
        organisationName={invitationInfo.organisationName}
        onSuccess={handleProfileSuccess}
        onError={() => navigate(`/invitation/${id}/error`)}
        onDecline={() => navigate(`/invitation/${id}/declined`)}
        initialGivenName={invitationInfo.givenName}
        initialFamilyName={invitationInfo.familyName}
      />
    </AuthLayout>
  );
};

export default ProfileCapturePage;
